import { keepPreviousData, useQuery } from "@tanstack/react-query"
import { Link, createFileRoute } from "@tanstack/react-router"
import { Archive, CalendarX, ChevronLeft } from "lucide-react"
import { useEffect, useState } from "react"

import { EventListPagination } from "@/components/guild-events-admin/event-list-pagination"
import { EventListRow } from "@/components/guild-events-admin/event-list-row"
import { PageIntroSplitHeader } from "@/components/layout/page-intro-split-header"
import { EmptyState } from "@/components/shared/empty-state"
import { SectionKicker } from "@/components/shared/section-kicker"
import { StatusAlert } from "@/components/shared/status-alert"
import { buttonVariants } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { TablePageSkeleton } from "@/components/ui/page-skeletons"
import { guildEventsApi } from "@/lib/guild-events-api"
import { guildEventsKeys } from "@/lib/query-keys"

export const Route = createFileRoute("/_authed/_admin/guild-events/archived")({
  component: GuildEventsArchivedRoute,
})

type SearchState = {
  status?: string
  bucket?: string
  query?: string
  page?: number
  pageSize?: number
}

function GuildEventsArchivedRoute() {
  const [pagination, setPagination] = useState<SearchState>({
    page: 1,
    pageSize: 12,
  })
  const page = pagination.page ?? 1
  const pageSize = pagination.pageSize ?? 12
  const pageQuery = useQuery({
    queryKey: guildEventsKeys.adminList({
      status: "archived",
      page,
      pageSize,
    }),
    queryFn: () =>
      guildEventsApi.listAdminEvents({ status: "archived", page, pageSize }),
    placeholderData: keepPreviousData,
  })
  const pageCount = Math.max(
    1,
    Math.ceil((pageQuery.data?.total ?? 0) / pageSize),
  )
  const safePage = Math.min(page, pageCount)

  useEffect(() => {
    if (page !== safePage) {
      setPagination((prev) => ({ ...prev, page: safePage }))
    }
  }, [page, safePage])

  const handleNavigate = (
    updater: (prev: SearchState) => Partial<SearchState>,
  ) => {
    setPagination((prev) => ({ ...prev, ...updater(prev) }))
  }

  if (pageQuery.isLoading) {
    return <TablePageSkeleton />
  }

  if (pageQuery.isError) {
    return (
      <StatusAlert tone="danger">
        <div className="flex items-center justify-between gap-3">
          <span>Failed to load archived events.</span>
          <button
            type="button"
            className="shrink-0 text-sm font-medium underline underline-offset-2 hover:no-underline"
            onClick={() => void pageQuery.refetch()}
          >
            Retry
          </button>
        </div>
      </StatusAlert>
    )
  }

  const pageItems = pageQuery.data?.items ?? []

  return (
    <div className="grid gap-4 md:gap-5">
      <PageIntroSplitHeader
        headerClassName="px-0"
        layoutClassName="max-w-4xl"
        leftPanelClassName="gap-3 rounded-2xl bg-card/70 p-4 md:p-5"
        titleClassName="text-2xl md:text-3xl"
        descriptionClassName="max-w-2xl leading-5"
        kicker={<SectionKicker icon={<Archive />}>Guild events</SectionKicker>}
        title="Archived events"
        description="Browse events that are no longer active."
        actions={
          <Link
            to="/guild-events"
            className={buttonVariants({
              variant: "ghost",
              size: "sm",
              className: "text-muted-foreground hover:text-foreground",
            })}
          >
            <ChevronLeft className="size-4" />
            Events
          </Link>
        }
      />

      <Card>
        <CardContent className="pt-5">
          {pageItems.length === 0 ? (
            <EmptyState
              icon={<CalendarX className="size-4" />}
              title="No archived events"
              description="There are no archived guild events."
            />
          ) : null}

          <div className="grid gap-2">
            {pageItems.map((event) => (
              <EventListRow key={event.id} event={event} />
            ))}
          </div>

          <EventListPagination
            safePage={safePage}
            pageCount={pageCount}
            totalCount={pageQuery.data?.total ?? 0}
            onNavigate={handleNavigate}
          />
        </CardContent>
      </Card>
    </div>
  )
}
